/**
 * Fighter names from a Polymarket fight title, for a fight row that has no
 * Kalshi side to name it.
 *
 * Kalshi names each fighter in its own market's `subtitle`; a Poly-only fight
 * has no such market, and its row codes are Gamma's slug codes (`BRE1`,
 * `CHR20`) or, when the producer keyed it, full surnames. The Gamma title
 * carries both complete names, first-named = outcomes[0] = YES:
 *
 *   "UFC Fight Night: Brendan Allen vs. Christian Leroy Duncan (Middleweight, Main Card)"
 *
 * Display only. The names are used only when each one agrees with its side's
 * row code, so a title whose order disagrees with the slug can never put a
 * name beside the other fighter's price; anything else keeps the codes.
 */

const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);
const VS_SEPARATOR = /\s+vs\.?\s+/i;

/** "UFC Fight Night: A vs. B (Middleweight, Main Card)" -> ["A", "B"]. */
export function fightTitleNames(title: string | null | undefined): [string, string] | null {
  if (!title) return null;
  const colon = title.lastIndexOf(': ');
  const body = (colon >= 0 ? title.slice(colon + 2) : title).replace(/\s*\([^()]*\)\s*$/, '');
  const sides = body.split(VS_SEPARATOR).map((s) => s.trim());
  if (sides.length !== 2 || !sides[0] || !sides[1]) return null;
  return [sides[0], sides[1]];
}

/** Accent-folded, lowercased name tokens with generational suffixes dropped. */
export function fightNameTokens(name: string): string[] {
  const tokens = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .split(/[\s-]+/)
    .map((t) => t.replace(/[^a-z]/g, ''))
    .filter(Boolean);
  while (tokens.length > 1 && NAME_SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop();
  return tokens;
}

/** Whether a row code (`BRE1`, `MWELLM`, `Allen`) names this fighter: its
 *  letters open one of the name's tokens, the joined name, or the first
 *  initial followed by the surname. */
function codeNamesFighter(code: string, name: string): boolean {
  const letters = code.replace(/[^a-z]/gi, '').toLowerCase();
  if (letters.length < 2) return false;
  const tokens = fightNameTokens(name);
  if (tokens.length === 0) return false;
  if (tokens.some((t) => t.startsWith(letters))) return true;
  if (tokens.join('').startsWith(letters)) return true;
  return tokens.length > 1 && `${tokens[0][0]}${tokens[tokens.length - 1]}`.startsWith(letters);
}

/**
 * The [YES, NO] fighter names for a Poly fight title, or null when the title
 * does not parse or either name disagrees with its side's code.
 */
export function polyFightSideNames(
  title: string | null | undefined,
  yesCode: string,
  noCode: string,
): [string, string] | null {
  const names = fightTitleNames(title);
  if (!names) return null;
  if (!codeNamesFighter(yesCode, names[0]) || !codeNamesFighter(noCode, names[1])) return null;
  return names;
}
